import { ReactElement } from 'react';

import { IconProps } from '@components/icons';
import { IconCheckmark } from '@components/icons/checkmark';
import { IconEmail } from '@components/icons/email';
import { IconSms } from '@components/icons/phone-sms';
import { Stack } from '@components/stack';
import { StatusIndicator } from '@components/status-indicator';
import { useScopedTranslate } from '@locale/translate';
import { pxToRem } from '@utils/converters';

import { TravellerActionGroupAction } from 'api/traveller-actions/requests';
import { getActionRatio, getActionStatus } from 'ui/authenticated/main/helpers';
import { TravellerNodeActionCard } from 'ui/journey-builder/node-types/node-container';
import { useFlowNodes } from 'ui/traveller-journey-builder/hooks/use-nodes';
import { NodeActions } from 'ui/traveller-journey-builder/node-types/common';
import { NodeContainer } from 'ui/traveller-journey-builder/node-types/node-container';
import { TravellerJourneyNode } from 'ui/traveller-journey-builder/node-types/types';

import { isActionError } from '../common';

type ActionType = 'combo_send_form';

const GroupActionStatus = ({
  action,
  icon,
}: {
  action: TravellerActionGroupAction;
  icon: ReactElement<IconProps>;
}) => {
  const isError = isActionError(action);
  const ratio = getActionRatio(action);
  const status = getActionStatus(action);
  const variant =
    status === 'completed'
      ? 'completed'
      : status === 'skipped'
      ? 'skipped'
      : isError
      ? 'error'
      : status === 'in_progress'
      ? 'inProgress'
      : 'notStarted';

  return <StatusIndicator variant={variant} icon={icon} ratio={ratio} />;
};

export const Node = (node: TravellerJourneyNode<ActionType>) => {
  const { t } = useScopedTranslate(nodeLocale);
  useFlowNodes();
  const emailAction = node.data.groupActions?.find((action) => action.action === 'send_email');
  const smsAction = node.data.groupActions?.find((action) => action.action === 'send_sms');
  const comboAction = {
    shortId: node.id,
    action: 'combo_send_form',
    isDisabled: false,
    status: node.data.status,
    result: node.data.result,
  } as TravellerActionGroupAction;
  const isError =
    (!emailAction && isActionError(smsAction)) ||
    (!smsAction && isActionError(emailAction)) ||
    (isActionError(emailAction) && isActionError(smsAction));

  return (
    <NodeContainer node={node}>
      <TravellerNodeActionCard
        node={node}
        label={t('title')}
        descriptionText={node.data.description}
        renderBelowDescription={
          <Stack orientation="horizontal" style={{ paddingTop: pxToRem(8) }}>
            {emailAction && !emailAction.isDisabled && (
              <GroupActionStatus icon={<IconEmail />} action={emailAction} />
            )}
            {smsAction && !smsAction.isDisabled && (
              <GroupActionStatus icon={<IconSms />} action={smsAction} />
            )}
            <GroupActionStatus icon={<IconCheckmark />} action={comboAction} />
          </Stack>
        }
        isError={isError}
      >
        <NodeActions node={node} overwriteError={isError} />
      </TravellerNodeActionCard>
    </NodeContainer>
  );
};

const nodeLocale = {
  en: {
    title: 'Send form',
  },
};
